import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { MobileGoogleDriveAuth } from '../services/mobileGoogleDriveAuth';
import { isNativeApp } from '../utils/platformUtils';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { Capacitor } from '@capacitor/core';

interface GoogleDriveContextType {
  isReady: boolean;
  isAuthorized: boolean;
  getAccessToken: (options?: { interactive?: boolean }) => Promise<string>;
  uploadFile: (params: { file: Blob; filename: string; mimeType: string }) => Promise<any>;
  listFiles: (params?: { pageSize?: number }) => Promise<Array<{ id: string; name: string; mimeType?: string; modifiedTime?: string; size?: string; webViewLink?: string }>>;
}

const GoogleDriveContext = createContext<GoogleDriveContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

const CLIENT_ID = '400451062835-ksi007ob0a6o5dvvkmvtipovogv62b3j';
const SCOPE = 'https://www.googleapis.com/auth/drive.file';

export const GoogleDriveProvider = ({ children }: ProviderProps) => {
  const [isReady, setIsReady] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const cachedTokenRef = useRef<string | null>(null);
  const cachedExpiryEpochSecRef = useRef<number | null>(null);

  const getAccessToken = useCallback(async (options?: { interactive?: boolean }): Promise<string> => {
    const interactive = options?.interactive === true;
    
    // Check cached token first
    const nowSec = Math.floor(Date.now() / 1000);
    const bufferSec = 60; // refresh a minute early
    if (
      cachedTokenRef.current &&
      cachedExpiryEpochSecRef.current &&
      cachedExpiryEpochSecRef.current - nowSec > bufferSec
    ) {
      return Promise.resolve(cachedTokenRef.current);
    }

    // Use MobileGoogleDriveAuth service which handles Byteowls OAuth2 plugin
    try {
      const mobileAuth = MobileGoogleDriveAuth.getInstance();
      const token = await mobileAuth.getAccessToken(interactive);
      
      // Store token in cache
      cachedTokenRef.current = token;
      const expiresIn = 3600; // Default expiry
      cachedExpiryEpochSecRef.current = nowSec + expiresIn;
      
      // Store in sessionStorage for persistence
      try {
        sessionStorage.setItem('gd_access_token', token);
        sessionStorage.setItem('gd_access_expiry', String(cachedExpiryEpochSecRef.current));
      } catch {}
      
      setIsAuthorized(true);
      return token;
    } catch (error: any) {
      console.error('[GoogleDrive] OAuth authentication error:', error);
      setIsAuthorized(false);
      if (error.error) {
        throw new Error(error.error || 'OAuth authentication failed');
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    // Initialize - try to restore cached token
    setIsReady(true);
    
    // Try to restore cached token from sessionStorage
    try {
      const storedToken = sessionStorage.getItem('gd_access_token');
      const storedExp = Number(sessionStorage.getItem('gd_access_expiry') || '0');
      const nowSec = Math.floor(Date.now() / 1000);
      if (storedToken && storedExp && storedExp - nowSec > 60) {
        cachedTokenRef.current = storedToken;
        cachedExpiryEpochSecRef.current = storedExp;
        setIsAuthorized(true);
      }
    } catch (error) {
      console.warn('[GoogleDrive] Error restoring cached token:', error);
    }
  }, []);

  // Attempt silent token acquisition on readiness if not already authorized
  // Only try if user is signed in
  useEffect(() => {
    if (!isReady || isAuthorized) return;
    
    // Check if user is signed in first
    const bearerToken = localStorage.getItem('bearer_token');
    if (!bearerToken) {
      // User not signed in yet - don't try Google Drive auth
      return;
    }
    
    // Try to get token silently (non-interactive)
    getAccessToken({ interactive: false })
      .then(() => setIsAuthorized(true))
      .catch((error) => {
        // Silent auth failed - user will need to authenticate interactively
        // This is expected if no valid Google Drive session exists
        // Only log if it's not the "Please log in first" error
        if (!error.message?.includes('log in')) {
          console.log('[GoogleDrive] Silent auth failed:', error.message);
        }
      });
  }, [isReady, isAuthorized, getAccessToken]);

  const uploadFile = useCallback(async (params: { file: Blob; filename: string; mimeType: string }) => {
    const { file, filename, mimeType } = params;
    const accessToken = await getAccessToken({ interactive: false });

    const metadata = { name: filename } as Record<string, any>;
    const boundary = 'mhsa_' + Math.random().toString(36).slice(2);
    const delimiter = `--${boundary}`;
    const closeDelimiter = `--${boundary}--`;
    const bodyParts: (string | ArrayBuffer)[] = [];

    bodyParts.push(
      `${delimiter}\r\n` +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        JSON.stringify(metadata) +
        '\r\n'
    );
    bodyParts.push(
      `${delimiter}\r\n` +
        `Content-Type: ${mimeType}\r\n\r\n`
    );
    const arrayBuffer = await file.arrayBuffer();
    bodyParts.push(arrayBuffer);
    bodyParts.push(`\r\n${closeDelimiter}`);

    const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: new Blob(bodyParts as BlobPart[], { type: `multipart/related; boundary=${boundary}` }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Drive upload failed: ${res.status} ${text}`);
    }
    return res.json();
  }, [getAccessToken]);

  const listFiles = useCallback(async (params?: { pageSize?: number }) => {
    const accessToken = await getAccessToken({ interactive: false });
    const pageSize = params?.pageSize ?? 100;
    const url = `https://www.googleapis.com/drive/v3/files?pageSize=${pageSize}&fields=files(id,name,mimeType,modifiedTime,size,webViewLink)`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Drive list failed: ${res.status} ${text}`);
    }
    const data = await res.json();
    return (data?.files ?? []) as Array<{ id: string; name: string; mimeType?: string; modifiedTime?: string; size?: string; webViewLink?: string }>;
  }, [getAccessToken]);

  const value = useMemo<GoogleDriveContextType>(
    () => ({ isReady, isAuthorized, getAccessToken, uploadFile, listFiles }),
    [isReady, isAuthorized, getAccessToken, uploadFile, listFiles]
  );

  return (
    <GoogleDriveContext.Provider value={value}>
      {children}
    </GoogleDriveContext.Provider>
  );
};

export function useGoogleDrive() {
  const ctx = useContext(GoogleDriveContext);
  if (!ctx) throw new Error('useGoogleDrive must be used within GoogleDriveProvider');
  return ctx;
}


